import React from "react";
import Badge from "../ui/badge/Badge";

interface Column<T> {
  key: string;
  header: string;
  field: string;
  headerClassName?: string;
  className?: string;
  cell?: (row: T) => React.ReactNode;
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
}

const SimpleLoader = () => (
  <div className="flex items-center justify-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-primary"></div>
  </div>
);

const Table = <T extends object>({
  data = [],
  columns = [],
  loading = false
}: TableProps<T>) => {
  if (loading) {
    return <SimpleLoader />;
  }

  if (!columns || columns.length === 0) {
    throw new Error('No columns defined');
  }

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-card px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1">
      <div className="max-w-full overflow-x-auto">
        <table className="w-full table-auto">
          <thead className="border-b border-border-primary">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`text-left py-4 px-4 font-medium text-text-primary ${
                    column.headerClassName || ''
                  }`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="py-4">
                  <span className="flex justify-center items-center text-text-secondary my-16 text-2xl">No data available</span>
                </td>
              </tr>
            )}

            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((column) => (
                  <td
                    key={`${rowIndex}-${column.key}`}
                    className={`border-b border-border-primary py-5 px-4 ${
                      rowIndex === data.length - 1 ? 'border-b-0' : ''
                    } ${column.className || ''}`}
                  >
                    {column.cell ? column.cell(row) : String(row[column.field as keyof T])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
